/**
 * Source de lecture pour une image disque servie par le serveur.
 *
 * Le chemin local lit l'ISO par tranches via File.slice() : rien n'est charge
 * en memoire tant que l'emulateur ne demande pas le secteur. Cette classe offre
 * le meme comportement pour une URL, en s'appuyant sur les requetes de plage
 * HTTP (en-tete Range).
 *
 * Sans cela, une image disque de 1,4 Go etait entierement telechargee en
 * memoire avant le moindre secteur lu -- l'onglet saturait avant le demarrage.
 *
 * Deux precautions rendent l'approche viable :
 *
 *  - LECTURE PAR GROUPES. Un jeu lit par blocs de 2 Ko et revient sans cesse
 *    sur les memes zones (tables de fichiers, code deja charge). Une requete
 *    par lecture ajouterait un aller-retour reseau a chaque acces. On lit donc
 *    par groupes de 256 Ko et on conserve les plus recents.
 *
 *  - COALESCENCE. Plusieurs lectures voisines arrivent souvent en rafale ; on
 *    partage la requete en cours plutot que d'en emettre une par appel.
 */

/** Taille d'un groupe. Compromis entre nombre de requetes et memoire retenue. */
const CHUNK_SIZE = 256 * 1024;

/** Nombre de groupes conserves, soit 16 Mo au plus. */
const MAX_CHUNKS = 64;

export default class RemoteDiscSource {
	readonly url: string;
	readonly size: number;
	/** Groupes deja recuperes, du plus ancien au plus recent (ordre Map). */
	private chunks = new Map<number, Uint8Array>();
	/** Requetes en vol, pour ne pas demander deux fois le meme groupe. */
	private pending = new Map<number, Promise<Uint8Array>>();

	private constructor(url: string, size: number) {
		this.url = url;
		this.size = size;
	}

	/**
	 * Prepare la source et verifie que le serveur honore bien les plages.
	 *
	 * On ne se fie pas a `Accept-Ranges` seul : certains intermediaires
	 * l'annoncent puis renvoient le fichier entier. La verification consiste a
	 * demander un octet et a exiger un code 206.
	 */
	static async open(url: string): Promise<RemoteDiscSource> {
		const probe = await fetch(url, { headers: { Range: 'bytes=0-0' } });
		// Une image protegee renvoie 401/403 : c'est un defaut d'authentification,
		// pas un defaut de serveur. Le dire clairement, sinon le message parle de
		// plages HTTP alors que la cause est ailleurs.
		if (probe.status === 401 || probe.status === 403) {
			throw new Error(
				"Accès refusé à l’image disque. Ouvrez son adresse directement dans " +
					'le navigateur pour vous authentifier, puis revenez sur cette page.',
			);
		}
		if (probe.status !== 206) {
			throw new Error(
				`Le serveur ne gère pas les requêtes de plage sur ${url} ` +
					`(réponse ${probe.status}). L'image disque ne peut pas être lue à la demande.`,
			);
		}
		// « bytes 0-0/1503199232 » : la taille totale est apres la barre.
		const range = probe.headers.get('Content-Range') ?? '';
		const total = Number(range.split('/')[1]);
		if (!Number.isFinite(total) || total <= 0) {
			throw new Error(`Taille inconnue pour ${url} (Content-Range: « ${range} »).`);
		}
		// Le corps de la sonde ne sert a rien ; on le consomme pour liberer la
		// connexion plutot que de la laisser ouverte.
		await probe.arrayBuffer().catch(() => undefined);
		return new RemoteDiscSource(url, total);
	}

	private async fetchChunk(index: number): Promise<Uint8Array> {
		const start = index * CHUNK_SIZE;
		const end = Math.min(start + CHUNK_SIZE, this.size) - 1;
		const response = await fetch(this.url, { headers: { Range: `bytes=${start}-${end}` } });
		if (response.status !== 206) {
			throw new Error(`Plage refusée (${response.status}) sur ${this.url}.`);
		}
		return new Uint8Array(await response.arrayBuffer());
	}

	private async chunk(index: number): Promise<Uint8Array> {
		const held = this.chunks.get(index);
		if (held) {
			// Remise en fin de Map : les groupes evinces sont les moins recents.
			this.chunks.delete(index);
			this.chunks.set(index, held);
			return held;
		}
		const inFlight = this.pending.get(index);
		if (inFlight) return inFlight;

		const request = this.fetchChunk(index)
			.then((data) => {
				this.chunks.set(index, data);
				while (this.chunks.size > MAX_CHUNKS) {
					const oldest = this.chunks.keys().next();
					if (oldest.done) break;
					this.chunks.delete(oldest.value);
				}
				return data;
			})
			.finally(() => {
				this.pending.delete(index);
			});
		this.pending.set(index, request);
		return request;
	}

	/**
	 * Lit `size` octets a partir de `offset`, en assemblant les groupes
	 * necessaires. Une lecture chevauchant deux groupes est recousue ici.
	 */
	async read(offset: number, size: number): Promise<Uint8Array> {
		const out = new Uint8Array(size);
		let written = 0;
		while (written < size) {
			const position = offset + written;
			const index = Math.floor(position / CHUNK_SIZE);
			const within = position - index * CHUNK_SIZE;
			const data = await this.chunk(index);
			const slice = data.subarray(within, Math.min(within + (size - written), data.length));
			if (slice.length === 0) break; // Lecture au-dela de la fin du fichier.
			out.set(slice, written);
			written += slice.length;
		}
		return written === size ? out : out.subarray(0, written);
	}
}
